<template>
  <div>
    <!-- ── Mockup bar with user switcher ── -->
    <div class="mockup-bar">
      <span class="mockup-badge">MOCKUP</span>
      <span class="mockup-bar-sep">·</span>
      <span class="mockup-bar-label">Viewing as:</span>
      <button
        v-for="u in users"
        :key="u.id"
        class="user-btn"
        :class="{ active: currentUser.id === u.id }"
        @click="currentUser = u"
      >{{ u.name }}</button>
    </div>

    <div class="page-content">

      <div class="instructions">
        <div class="line">Score the verbal fluency test for supermarket items.</div>
      </div>
      <div class="note">C = Correct, I = Incorrect, R = Repeat, Q = Query</div>

      <!-- ── Word entry grid ── -->
      <div class="text-input-grid">
        <div
          v-for="(entry, index) in entries"
          :key="entry.id"
          class="input-row"
        >
          <div class="input-row-main">
            <span class="input-number">{{ index + 1 }}.</span>

            <div class="input-container">
              <div class="line-input" :class="{ flagged: entry.flagged || entry.pendingRequest }">
                <div class="word-field">
                  <span class="mock-word-text">{{ entry.word }}</span>
                  <!-- change history indicator -->
                  <button
                    v-if="entry.changeLog.length"
                    class="history-btn"
                    :title="`${entry.changeLog.length} change(s)`"
                    @click="toggleHistory(index)"
                  >{{ entry.changeLog.length }} ✎</button>
                </div>
                <div class="toggle" @click.stop>
                  <div
                    v-for="opt in toggleOptions"
                    :key="opt.value"
                    class="option"
                    :class="{ active: isActive(entry, opt.value) }"
                    @click="setToggle(index, opt.value)"
                  >{{ opt.label }}</div>
                </div>
              </div>

              <!-- Change history panel -->
              <div v-if="expandedHistoryIndex === index && entry.changeLog.length" class="history-panel">
                <div class="history-title">Change history</div>
                <div v-for="h in entry.changeLog" :key="h.id" class="history-item">
                  <span class="history-dot" :style="{ background: h.actorColor }"></span>
                  <span class="history-actor">{{ h.actor }}</span>
                  <span class="history-change">
                    <span class="mark-pill" :class="h.from">{{ h.from || '—' }}</span>
                    →
                    <span class="mark-pill" :class="h.to">{{ h.to || '—' }}</span>
                  </span>
                  <span class="history-reason">{{ h.reason }}</span>
                  <span class="history-time">{{ h.time }}</span>
                </div>
              </div>
            </div>

            <!-- Comment bubble -->
            <button
              type="button"
              class="item-comment-button"
              :class="{ 'has-comments': entry.commentCount > 0 }"
              @click="toggleComments(index)"
              title="Item comments"
            >
              <span class="comment-icon">💬</span>
              <span v-if="entry.commentCount > 0" class="comment-count">{{ entry.commentCount }}</span>
            </button>

            <!-- Flag button -->
            <button
              type="button"
              class="item-comment-button flag-button"
              :class="{ 'is-flagged': entry.flagged, 'is-posted': entry.pendingRequest }"
              :title="entry.pendingRequest ? 'Change request open' : entry.flagged ? 'Cancel flag' : 'Request score change'"
              @click="toggleFlag(index)"
            >
              <span>{{ (entry.pendingRequest || entry.flagged) ? '⚑' : '⚐' }}</span>
            </button>
          </div>

          <!-- Inline item comment thread -->
          <div v-if="expandedCommentIndex === index" class="item-comment-panel-wrap">
            <div class="item-comment-panel">
              <div class="mini-thread-header">
                <span>Comments on "{{ entry.word }}"</span>
                <button class="mini-close" @click="expandedCommentIndex = null">Close</button>
              </div>
              <div v-if="entry.itemComments.length === 0" class="mini-empty">No comments yet.</div>
              <div
                v-for="c in entry.itemComments"
                :key="c.id"
                class="mini-comment"
                :class="{ 'mini-request': c.isRequest }"
              >
                <div class="mini-avatar" :style="{ background: c.avatarColor }">{{ c.initials }}</div>
                <div class="mini-body">
                  <div class="mini-meta">
                    <span class="mini-author">{{ c.author }}</span>
                    <span v-if="c.isRequest" class="mini-request-badge">⚑ Change request</span>
                    <span class="mini-time">{{ c.time }}</span>
                  </div>
                  <div class="mini-text">{{ c.text }}</div>
                  <div v-if="c.isRequest && (c.currentMark || c.suggestedMark)" class="mini-marks">
                    <span class="mark-chip">Was: {{ c.currentMark || '—' }}</span>
                    <span v-if="c.suggestedMark" class="mark-chip suggested">Suggested: {{ c.suggestedMark }}</span>
                    <template v-if="!c.resolved">
                      <button class="accept-btn" @click="acceptRequest(c)">✓ Accept</button>
                      <button class="reject-btn" @click="rejectRequest(c)">✕ Reject</button>
                    </template>
                    <span v-else class="mark-chip resolved">{{ c.resolvedAction === 'Accepted' ? '✓' : '✕' }} {{ c.resolvedAction }}</span>
                  </div>
                </div>
              </div>
              <div class="mini-new">
                <textarea v-model="entry.newComment" class="mini-input" placeholder="Add a comment..." rows="2"></textarea>
                <button class="mini-submit" :disabled="!entry.newComment.trim()" @click="postComment(index)">Post</button>
              </div>
            </div>
          </div>

          <!-- Inline change-request composer -->
          <div v-if="entry.flagged && !entry.pendingRequest" class="request-form">
            <div class="request-form-row">
              <span class="request-label">Suggest change to:</span>
              <div class="toggle small">
                <div
                  v-for="opt in toggleOptions"
                  :key="opt.value"
                  class="option"
                  :class="{ active: entry.suggestedMark === opt.value }"
                  @click="entry.suggestedMark = entry.suggestedMark === opt.value ? null : opt.value"
                >{{ opt.label }}</div>
              </div>
              <span class="request-divider">|</span>
              <span class="request-label">Reason:</span>
              <input
                v-model="entry.requestNote"
                class="request-input"
                placeholder="Brief reason..."
                type="text"
              />
              <button class="request-submit" :disabled="!entry.requestNote.trim()" @click="postRequest(index)">Post</button>
              <button class="request-cancel" @click="cancelFlag(index)">Cancel</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Entry Summary ── -->
      <div class="summary-section">
        <h3>Entry Summary</h3>
        <div class="summary-grid">
          <div class="summary-item">
            <span class="summary-label">Correct (C):</span>
            <span class="summary-count">{{ counts.c }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Incorrect (I):</span>
            <span class="summary-count">{{ counts.i + counts.ir }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Repeat (R):</span>
            <span class="summary-count">{{ counts.r + counts.ir }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Query (Q):</span>
            <span class="summary-count">{{ counts.q }}</span>
          </div>
          <div v-if="openRequestCount > 0" class="summary-item">
            <span class="summary-label open-requests">⚑ Open requests:</span>
            <span class="summary-count open-requests">{{ openRequestCount }}</span>
          </div>
        </div>
      </div>

      <!-- ── Task-level Comment Thread ── -->
      <div class="comment-thread">
        <h3 class="comment-header">Comments</h3>

        <div class="comments-container">
          <div v-if="nonRequestTaskComments.length === 0" class="no-comments">No comments yet.</div>
          <div
            v-for="c in nonRequestTaskComments"
            :key="c.id"
            class="comment-item"
            :class="{ 'is-resolved': c.resolved }"
          >
            <div class="comment-avatar" :style="{ backgroundColor: c.avatarColor }">{{ c.initials }}</div>
            <div class="comment-body">
              <div class="comment-header-info">
                <span class="comment-author">{{ c.author }}</span>
                <span class="comment-date">{{ c.time }}</span>
              </div>
              <div class="comment-text">{{ c.text }}</div>
            </div>
          </div>
        </div>

        <div class="new-comment-form">
          <textarea
            v-model="newTaskComment"
            placeholder="Add a comment..."
            class="comment-input"
            rows="3"
          ></textarea>
          <button class="submit-button" :disabled="!newTaskComment.trim()" @click="postTaskComment">Post Comment</button>
        </div>
      </div>

      <button class="submit-page-btn">Submit</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WireframesPage',
  data() {
    return {
      expandedCommentIndex: null,
      expandedHistoryIndex: null,
      newTaskComment: '',
      nextId: 200,
      toggleOptions: [
        { label: 'C', value: 'c' },
        { label: 'I', value: 'i' },
        { label: 'R', value: 'r' },
        { label: 'Q', value: 'q' }
      ],
      users: [
        { id: 1, name: 'Interviewer', role: '',         initials: 'IN', color: '#317391' },
        { id: 2, name: 'Reviewer',    role: '',         initials: 'RV', color: '#7c3aed' }
      ],
      currentUser: { id: 1, name: 'Interviewer', role: '', initials: 'IN', color: '#317391' },
      entries: [
        {
          id: 1, word: 'apple', selectedAnswer: 'c', aiSuggested: true,
          flagged: false, pendingRequest: false, suggestedMark: null, requestNote: '',
          commentCount: 0, newComment: '', changeLog: [],
          itemComments: []
        },
        { id: 2,  word: 'milk',        selectedAnswer: 'c',  aiSuggested: true,  flagged: false, pendingRequest: false, suggestedMark: null, requestNote: '', commentCount: 0, newComment: '', changeLog: [], itemComments: [] },
        { id: 3,  word: 'bread',       selectedAnswer: 'c',  aiSuggested: false, flagged: false, pendingRequest: false, suggestedMark: null, requestNote: '', commentCount: 0, newComment: '', changeLog: [], itemComments: [] },
        { id: 4,  word: 'apple juice', selectedAnswer: 'c',  aiSuggested: false, flagged: false, pendingRequest: false, suggestedMark: null, requestNote: '', commentCount: 0, newComment: '', changeLog: [], itemComments: [] },
        { id: 5,  word: 'eggs',        selectedAnswer: 'c',  aiSuggested: true,  flagged: false, pendingRequest: false, suggestedMark: null, requestNote: '', commentCount: 0, newComment: '', changeLog: [], itemComments: [] },
        { id: 6,  word: 'butter',      selectedAnswer: 'c',  aiSuggested: false, flagged: false, pendingRequest: false, suggestedMark: null, requestNote: '', commentCount: 0, newComment: '', changeLog: [], itemComments: [] },
        { id: 7,  word: 'cheese',      selectedAnswer: 'c',  aiSuggested: true,  flagged: false, pendingRequest: false, suggestedMark: null, requestNote: '', commentCount: 0, newComment: '', changeLog: [], itemComments: [] },
        { id: 8,  word: 'apple',       selectedAnswer: 'r',  aiSuggested: false, flagged: false, pendingRequest: false, suggestedMark: null, requestNote: '', commentCount: 0, newComment: '', changeLog: [], itemComments: [] },
        { id: 9,  word: 'cereal',      selectedAnswer: 'c',  aiSuggested: true,  flagged: false, pendingRequest: false, suggestedMark: null, requestNote: '', commentCount: 0, newComment: '', changeLog: [], itemComments: [] },
        { id: 10, word: 'yogurt',      selectedAnswer: 'q',  aiSuggested: false, flagged: false, pendingRequest: false, suggestedMark: null, requestNote: '', commentCount: 0, newComment: '', changeLog: [], itemComments: [] },
        { id: 11, word: 'soda',        selectedAnswer: 'i',  aiSuggested: false, flagged: false, pendingRequest: false, suggestedMark: null, requestNote: '', commentCount: 0, newComment: '', changeLog: [], itemComments: [] },
        { id: 12, word: 'tomatoes',    selectedAnswer: 'c',  aiSuggested: true,  flagged: false, pendingRequest: false, suggestedMark: null, requestNote: '', commentCount: 0, newComment: '', changeLog: [], itemComments: [] },
      ],
      taskComments: []
    };
  },
  computed: {
    counts() {
      const s = { c: 0, i: 0, r: 0, q: 0, ir: 0 };
      this.entries.forEach(e => { if (e.selectedAnswer && e.selectedAnswer in s) s[e.selectedAnswer]++; });
      return s;
    },
    nonRequestTaskComments() {
      return this.taskComments.filter(c => !c.isRequest);
    },
    openRequestCount() {
      return this.entries.reduce((n, e) => n + e.itemComments.filter(c => c.isRequest && !c.resolved).length, 0);
    }
  },
  methods: {
    // ── Toggle helpers ──
    isActive(entry, optValue) {
      if (!entry.selectedAnswer) return false;
      if (optValue === 'i') return entry.selectedAnswer === 'i' || entry.selectedAnswer === 'ir';
      if (optValue === 'r') return entry.selectedAnswer === 'r' || entry.selectedAnswer === 'ir';
      return entry.selectedAnswer === optValue;
    },
    setToggle(index, value) {
      const e = this.entries[index];
      const prev = e.selectedAnswer;
      let next;
      if (value === 'c' || value === 'q') {
        next = prev === value ? null : value;
      } else {
        if (prev === value) next = null;
        else if (!prev) next = value;
        else if (prev === 'i' || prev === 'r') next = 'ir';
        else if (prev === 'ir') next = value === 'i' ? 'r' : 'i';
        else next = value;
      }
      if (next !== prev) {
        e.changeLog.push({
          id: `h_${this.nextId++}`,
          actor: this.currentUser.name,
          actorColor: this.currentUser.color,
          from: prev ? prev.toUpperCase() : '—',
          to: next ? next.toUpperCase() : '—',
          reason: 'Manual edit',
          time: 'Just now'
        });
      }
      e.selectedAnswer = next;
    },

    // ── Comment panel ──
    toggleComments(index) {
      this.expandedCommentIndex = this.expandedCommentIndex === index ? null : index;
    },

    // ── History panel ──
    toggleHistory(index) {
      this.expandedHistoryIndex = this.expandedHistoryIndex === index ? null : index;
    },

    // ── Flag / request ──
    toggleFlag(index) {
      const e = this.entries[index];
      if (e.pendingRequest) return;
      e.flagged = !e.flagged;
      if (!e.flagged) { e.suggestedMark = null; e.requestNote = ''; }
    },
    cancelFlag(index) {
      const e = this.entries[index];
      e.flagged = false; e.suggestedMark = null; e.requestNote = '';
    },
    postRequest(index) {
      const e = this.entries[index];
      if (!e.requestNote.trim()) return;
      const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const req = {
        id: `req_${this.nextId++}`,
        author: this.currentUser.name,
        initials: this.currentUser.initials,
        avatarColor: this.currentUser.color,
        time: now,
        isRequest: true, resolved: false,
        word: e.word,
        entryIndex: index,
        currentMark: e.selectedAnswer ? e.selectedAnswer.toUpperCase() : '—',
        suggestedMark: e.suggestedMark ? e.suggestedMark.toUpperCase() : null,
        text: e.requestNote.trim()
      };
      e.itemComments.push({ ...req });
      e.commentCount = e.itemComments.length;
      this.taskComments.push({ ...req });
      e.pendingRequest = true;
      e.flagged = false;
      this.expandedCommentIndex = index;
    },

    // ── Accept / Reject request ──
    acceptRequest(comment) {
      const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      comment.resolved = true;
      comment.resolvedAction = 'Accepted';
      comment.resolvedBy = this.currentUser.name;

      // Apply the suggested mark to the entry
      const entry = this.entries[comment.entryIndex];
      if (entry && comment.suggestedMark) {
        const prev = entry.selectedAnswer;
        const next = comment.suggestedMark.toLowerCase();
        entry.selectedAnswer = next;
        entry.changeLog.push({
          id: `h_${this.nextId++}`,
          actor: this.currentUser.name,
          actorColor: this.currentUser.color,
          from: prev ? prev.toUpperCase() : '—',
          to: comment.suggestedMark,
          reason: `Accepted request from ${comment.author}`,
          time: now
        });
      }
      if (entry) entry.pendingRequest = false;

      // Mirror resolved state into item comment thread
      if (entry) {
        const ic = entry.itemComments.find(x => x.id === comment.id);
        if (ic) { ic.resolved = true; ic.resolvedAction = 'Accepted'; }
      }

    },
    rejectRequest(comment) {
      comment.resolved = true;
      comment.resolvedAction = 'Rejected';
      comment.resolvedBy = this.currentUser.name;

      const entry = this.entries[comment.entryIndex];
      if (entry) entry.pendingRequest = false;

      if (entry) {
        const ic = entry.itemComments.find(x => x.id === comment.id);
        if (ic) { ic.resolved = true; ic.resolvedAction = 'Rejected'; }
      }

    },

    // ── Comments ──
    postComment(index) {
      const e = this.entries[index];
      if (!e.newComment.trim()) return;
      const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      e.itemComments.push({
        id: `c_${this.nextId++}`,
        author: this.currentUser.name,
        initials: this.currentUser.initials,
        avatarColor: this.currentUser.color,
        time: now, isRequest: false, text: e.newComment.trim()
      });
      e.commentCount = e.itemComments.length;
      e.newComment = '';
    },
    postTaskComment() {
      if (!this.newTaskComment.trim()) return;
      const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      this.taskComments.push({
        id: `tc_${this.nextId++}`,
        author: this.currentUser.name,
        initials: this.currentUser.initials,
        avatarColor: this.currentUser.color,
        time: now, isRequest: false, text: this.newTaskComment.trim()
      });
      this.newTaskComment = '';
    }
  }
};
</script>

<style scoped>
/* ── Mockup bar ─────────────────────────────────────────── */
.mockup-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 20px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  color: #6b7280;
}
.mockup-badge {
  font-size: 10px;
  font-weight: 700;
  background: #fef3c7;
  color: #92400e;
  padding: 1px 6px;
  border-radius: 3px;
  letter-spacing: 0.05em;
}
.mockup-bar-sep { color: #d1d5db; }
.mockup-bar-label { color: #9ca3af; }

/* ── User switcher ──────────────────────────────────────── */
.user-btn {
  padding: 3px 10px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  background: white;
  color: #374151;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
}
.user-btn:hover { border-color: #317391; color: #317391; }
.user-btn.active { background: #317391; color: white; border-color: #317391; }

/* ── Page content ───────────────────────────────────────── */
.page-content { padding: 20px; max-width: 1200px; margin: 0 auto; margin-top: 75px; }
.instructions { font-size: 18px; margin-bottom: 20px; }
.line { margin-bottom: 20px; }
.note { margin-left: 25px; margin-bottom: 30px; font-size: 14px; }

/* ── Word grid ──────────────────────────────────────────── */
.text-input-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  row-gap: 10px;
  column-gap: 20px;
}
@media (max-width: 1024px) { .text-input-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 768px)  { .text-input-grid { grid-template-columns: 1fr; } }

.input-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 10px;
  gap: 8px;
}
.input-row-main {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  gap: 0 8px;
}
.input-number {
  width: 30px;
  font-weight: bold;
  text-align: right;
  margin-right: 10px;
  flex-shrink: 0;
}
.input-container { position: relative; flex-grow: 1; min-width: 0; }

/* ── TextInputWithToggle replica ────────────────────────── */
.line-input {
  display: flex;
  align-items: center;
  padding: 8px 8px 8px 0;
  margin-bottom: 2px;
  gap: 8px;
  border-bottom: 1px solid #e5e7eb;
  transition: background 0.15s;
}
.line-input.flagged { background: #fffbeb; border-radius: 4px; padding-left: 6px; }
.word-field { flex: 1; display: flex; align-items: center; gap: 5px; min-width: 0; }
.mock-word-text { font-size: 15px; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ai-badge { font-size: 9px; font-weight: 800; background: #237091; color: white; padding: 1px 5px; border-radius: 8px; flex-shrink: 0; }

/* ── Change history button ──────────────────────────────── */
.history-btn {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
  padding: 1px 6px;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
}
.history-btn:hover { background: #fde68a; }

/* ── History panel ──────────────────────────────────────── */
.history-panel {
  margin-top: 4px;
  margin-left: 0;
  padding: 8px 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  font-size: 12px;
}
.history-title { font-weight: 600; color: #92400e; margin-bottom: 6px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
.history-item { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; flex-wrap: wrap; }
.history-item:last-child { margin-bottom: 0; }
.history-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.history-actor { font-weight: 600; color: #374151; }
.history-change { display: flex; align-items: center; gap: 4px; }
.mark-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #475569;
}
.mark-pill.C { background: #dcfce7; color: #166534; }
.mark-pill.I { background: #fee2e2; color: #991b1b; }
.mark-pill.R { background: #fef3c7; color: #92400e; }
.mark-pill.Q { background: #e0f2fe; color: #075985; }
.mark-pill\.— { background: #f1f5f9; color: #94a3b8; }
.history-reason { color: #6b7280; flex: 1; font-style: italic; }
.history-time { color: #9ca3af; white-space: nowrap; }

/* ── Toggle (matches FourWayToggle exactly) ─────────────── */
.toggle { display: flex; justify-content: space-between; width: 100px; border: 1px solid #3f8fb2; border-radius: 10px; overflow: hidden; flex-shrink: 0; }
.toggle.small { width: 88px; }
.option { flex: 1; text-align: center; line-height: 30px; cursor: pointer; background-color: white; color: #3f8fb2; border-right: 1px solid #3f8fb2; font-size: 13px; transition: background-color 0.3s ease; }
.option:last-child { border-right: none; }
.option.active { background-color: #4aa7d0; background-image: linear-gradient(to bottom, #4aa7d0, #357a96); color: white; }

/* ── Comment / flag buttons ─────────────────────────────── */
.item-comment-button {
  position: relative; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; padding: 0; border: 1px solid #e5e7eb; border-radius: 6px;
  background: #fff; cursor: pointer; color: #6b7280; font-size: 15px;
}
.item-comment-button:hover { background: #f9fafb; border-color: #4a90e2; color: #4a90e2; }
.item-comment-button.has-comments { background: #eff6ff; border-color: #4a90e2; color: #4a90e2; }
.comment-icon { font-size: 16px; }
.comment-count {
  position: absolute; font-size: 10px; font-weight: 600; color: #fff; background: #4a90e2;
  min-width: 16px; height: 16px; border-radius: 8px; display: flex; align-items: center;
  justify-content: center; top: -4px; right: -4px;
}
.flag-button { color: #d1d5db; font-size: 14px; }
.flag-button:hover { border-color: #f59e0b !important; color: #f59e0b !important; background: #fffbeb !important; }
.flag-button.is-flagged { border-color: #f59e0b; background: #fffbeb; color: #d97706; }
.flag-button.is-posted  { border-color: #fb923c; background: #fff7ed; color: #ea580c; }

/* ── Request form ───────────────────────────────────────── */
.request-form { flex: 1 1 100%; margin-top: 2px; margin-left: 40px; }
.request-form-row {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 8px 10px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; font-size: 13px;
}
.request-label { color: #78350f; font-weight: 500; white-space: nowrap; }
.request-divider { color: #d1d5db; }
.request-input { flex: 1; min-width: 120px; padding: 5px 8px; border: 1px solid #fde68a; border-radius: 4px; font-size: 13px; font-family: inherit; background: white; }
.request-input:focus { outline: none; border-color: #f59e0b; }
.request-submit { background: #d97706; color: white; border: none; padding: 5px 12px; border-radius: 4px; font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap; }
.request-submit:disabled { background: #d1d5db; cursor: not-allowed; }
.request-submit:not(:disabled):hover { background: #b45309; }
.request-cancel { background: none; border: 1px solid #d1d5db; color: #6b7280; padding: 5px 10px; border-radius: 4px; font-size: 13px; cursor: pointer; }

/* ── Item comment panel ─────────────────────────────────── */
.item-comment-panel-wrap { flex: 1 1 100%; display: flex; justify-content: flex-end; margin-top: 4px; }
.item-comment-panel { width: 440px; max-width: 100%; max-height: 320px; overflow: auto; background: #fff; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); border: 1px solid #e5e7eb; padding: 12px; }
.mini-thread-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; font-size: 13px; font-weight: 600; color: #374151; }
.mini-close { background: none; border: none; font-size: 12px; color: #6b7280; cursor: pointer; }
.mini-empty { font-size: 13px; color: #9ca3af; text-align: center; padding: 8px; }
.mini-comment { display: flex; gap: 8px; margin-bottom: 8px; padding: 8px; border-radius: 6px; border: 1px solid #e5e7eb; background: white; }
.mini-comment.mini-request { border-color: #fcd34d; background: #fffbeb; }
.mini-avatar { width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700; color: white; }
.mini-body { flex: 1; min-width: 0; }
.mini-meta { display: flex; align-items: center; gap: 6px; margin-bottom: 2px; flex-wrap: wrap; }
.mini-author { font-size: 12px; font-weight: 600; color: #111827; }
.mini-request-badge { font-size: 10px; background: #fef3c7; color: #92400e; padding: 1px 6px; border-radius: 8px; font-weight: 600; }
.mini-time { font-size: 11px; color: #9ca3af; margin-left: auto; }
.mini-text { font-size: 12px; color: #374151; }
.mini-marks { display: flex; gap: 6px; margin-top: 4px; flex-wrap: wrap; }
.mark-chip { font-size: 11px; background: #f1f5f9; color: #475569; padding: 1px 7px; border-radius: 8px; }
.mark-chip.suggested { background: #dcfce7; color: #166534; }
.mark-chip.resolved { background: #e0f2fe; color: #075985; }
.mini-new { padding-top: 8px; border-top: 1px solid #e5e7eb; display: flex; gap: 6px; align-items: flex-end; }
.mini-input { flex: 1; padding: 6px 8px; border: 1px solid #e5e7eb; border-radius: 6px; font-size: 12px; font-family: inherit; resize: none; }
.mini-input:focus { outline: none; border-color: #4a90e2; }
.mini-submit { background: #4a90e2; color: white; border: none; padding: 6px 12px; border-radius: 4px; font-size: 12px; cursor: pointer; white-space: nowrap; }
.mini-submit:disabled { background: #d1d5db; cursor: not-allowed; }

/* ── Entry Summary ──────────────────────────────────────── */
.summary-section { margin-top: 30px; padding: 15px; border: 1px solid #ddd; border-radius: 8px; background-color: #f9f9f9; }
.summary-section h3 { margin-top: 0; color: #237091; margin-bottom: 15px; }
.summary-grid { display: flex; flex-wrap: wrap; gap: 20px; }
.summary-item { display: flex; align-items: center; min-width: 150px; }
.summary-label { font-weight: bold; margin-right: 10px; font-size: 14px; }
.summary-label.open-requests { color: #d97706; }
.summary-count { font-size: 18px; color: #237091; }
.summary-count.open-requests { color: #d97706; }

/* ── Task-level CommentThread ───────────────────────────── */
.comment-thread { padding: 24px 0; border-top: 1px solid #e5e7eb; margin-top: 24px; }
.comment-header { font-size: 18px; font-weight: 600; margin-bottom: 20px; color: #111827; }
.comments-container { margin-bottom: 24px; }
.no-comments { padding: 24px; text-align: center; color: #9ca3af; font-size: 14px; background-color: #f9fafb; border-radius: 8px; }
.comment-item { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; padding: 14px 16px; background-color: #fff; border-radius: 8px; border: 1px solid #e5e7eb; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
.comment-item.is-request { border-color: #fcd34d; background: #fffbeb; }
.comment-item.is-resolved { opacity: 0.8; }
.comment-avatar { flex-shrink: 0; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 600; color: white; }
.comment-body { flex: 1; min-width: 0; }
.comment-header-info { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
.comment-author { font-weight: 600; font-size: 14px; color: #111827; }
.request-tag { font-size: 11px; font-weight: 600; background: #fef3c7; color: #92400e; padding: 2px 7px; border-radius: 10px; }
.resolved-inline-tag { font-size: 11px; font-weight: 600; padding: 2px 7px; border-radius: 10px; }
.comment-item.is-request .resolved-inline-tag { background: #dcfce7; color: #166534; }
.comment-date { font-size: 12px; color: #9ca3af; margin-left: auto; }
.comment-text { margin-bottom: 8px; line-height: 1.5; font-size: 14px; color: #374151; }
.request-chips { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; margin-top: 4px; }
.chip { font-size: 12px; background: #f1f5f9; color: #475569; padding: 2px 8px; border-radius: 10px; }
.chip.suggested { background: #dcfce7; color: #166534; }

/* Accept / Reject buttons */
.accept-btn {
  font-size: 12px; font-weight: 600; color: #166534;
  background: #dcfce7; border: 1px solid #86efac;
  padding: 3px 12px; border-radius: 10px; cursor: pointer;
}
.accept-btn:hover { background: #bbf7d0; }
.reject-btn {
  font-size: 12px; font-weight: 600; color: #991b1b;
  background: #fee2e2; border: 1px solid #fca5a5;
  padding: 3px 12px; border-radius: 10px; cursor: pointer;
}
.reject-btn:hover { background: #fecaca; }
.resolved-chip { font-size: 12px; font-weight: 600; color: #475569; background: #f1f5f9; padding: 3px 10px; border-radius: 10px; }

.new-comment-form { margin-top: 24px; padding-top: 24px; border-top: 1px solid #e5e7eb; }
.comment-input { width: 100%; padding: 12px 14px; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 14px; font-family: inherit; resize: vertical; margin-bottom: 12px; background-color: #fff; box-sizing: border-box; }
.comment-input:focus { outline: none; border-color: #4a90e2; box-shadow: 0 0 0 2px rgba(74,144,226,0.2); }
.submit-button { background-color: #4a90e2; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 14px; font-weight: 500; }
.submit-button:disabled { background-color: #ccc; cursor: not-allowed; }
.submit-button:not(:disabled):hover { background-color: #357abd; }

.submit-page-btn { display: block; margin: 24px 0 48px auto; background: #317391; color: white; border: none; padding: 10px 28px; border-radius: 6px; font-size: 15px; font-weight: 600; cursor: pointer; }
</style>
